import React from "react";
import { BarChart3Icon, CalendarCheckIcon, CalendarRangeIcon, ClockIcon, InfoIcon, PackageIcon, UsersIcon, BoxIcon } from "lucide-react";
import { ActionLink } from "./ActionLink";
import { MockupTag } from "./MockupTag";
import { images, wideHeroImages } from "../../data/images";
import { Fact, LinkItem } from "../../types/content";
interface PageHeroProps {
  title: string;
  lead: string;
  /** Omit for a title-only banner. */
  primary?: LinkItem;
  secondary?: LinkItem;
  image?: string;
  imageAlt?: string;
  /** Kept for page context; the clean hero derives its eyebrow from the parent crumb. */
  breadcrumbs?: LinkItem[];
  /** Small line above the title. Falls back to the parent breadcrumb label. */
  eyebrow?: string;
  /** Show the short description + secondary CTA under the banner (programme and destination pages). */
  showLead?: boolean;
  facts?: Fact[];
  factsMockup?: boolean;
  badge?: React.ReactNode;
}

// One standard hero for every inner page: a wide rounded photo banner with eyebrow, uppercase title and one CTA.
export function PageHero({
  title,
  lead,
  primary,
  secondary,
  image = images.classroom,
  imageAlt = '',
  breadcrumbs,
  eyebrow,
  showLead = false,
  facts,
  factsMockup = true,
  badge
}: PageHeroProps) {
  const kicker = eyebrow ?? (breadcrumbs && breadcrumbs.length > 1 ? breadcrumbs[breadcrumbs.length - 2].label : undefined);
  const hasDetails = showLead || facts && facts.length > 0;
  return <>
      <section className="w-full bg-white p-2 md:p-4" data-id="element-812" data-mp="2abo4o7vyr3kn">
        <div className="relative flex min-h-[380px] w-full flex-col overflow-hidden rounded-[28px] bg-ink md:min-h-[460px] md:rounded-[40px] lg:min-h-[540px]" data-id="element-813" data-mp="2f50o83lsck6j">
          <img src={wideHeroImages[image] ?? image} alt={imageAlt} className="absolute inset-0 h-full w-full object-cover object-[center_35%]" data-id="element-814" data-mp="25e60eusw5ciz" />
          <div className="absolute inset-0 bg-black/40" aria-hidden="true" data-id="element-815" data-mp="1rf6evw0oddr1" />
          <MockupTag note="Placeholder image" className="absolute right-5 top-5 z-10 md:right-8" data-id="element-816" data-mp="1vf6ql8we3aue" />
          <div className="relative z-10 mx-auto flex w-full max-w-content flex-1 flex-col justify-end px-6 pb-10 pt-16 md:px-10 md:pb-14" data-id="element-817" data-mp="1ykl3qh29962">
            {badge ? <div className="mb-3" data-id="element-818" data-mp="3tadkmqme6duk">{badge}</div> : null}
            {kicker ? <p className="type-h4 font-normal text-white" data-id="element-819" data-mp="3odvky55x55p3">{kicker}</p> : null}
            <h1 className="mt-2 max-w-4xl type-h1 text-white" data-id="element-820" data-mp="3jesfijm10xgl">{title}</h1>
            {primary ? <div className="mt-6" data-id="element-821" data-mp="kwn15frmrvrq">
                <ActionLink to={primary.to} data-id="element-822" data-mp="kvlqhcej1baj">{primary.label}</ActionLink>
              </div> : null}
          </div>
        </div>
      </section>

      {hasDetails ? <div className="w-full bg-white" data-id="element-823" data-mp="ax623h6vwg4q">
          <div className="mx-auto max-w-content px-5 pb-4 pt-8 md:px-8 md:pt-10" data-id="element-824" data-mp="b01yv26zwyrl">
            {showLead ? <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between lg:gap-12" data-id="element-825" data-mp="6yor2mkceli1">
                <p className="max-w-3xl type-body text-ink" data-id="element-826" data-mp="2vm3k6g297y5k">{lead}</p>
                {secondary ? <ActionLink to={secondary.to} variant="secondary" className="shrink-0 self-start lg:self-center" data-id="element-827" data-mp="174sk5ahudfji">
                    {secondary.label}
                  </ActionLink> : null}
              </div> : null}
            {facts && facts.length ? <div className={showLead ? 'mt-8 border-t border-line pt-8' : ''} data-id="element-828" data-mp="1g3xhwelkx4um">
                {factsMockup ? <MockupTag note="Sample details" className="mb-4" data-id="element-829" data-mp="1kxrxp6y2czof" /> : null}
                <FactList facts={facts} data-id="element-830" data-mp="1pw36decmh452" />
              </div> : null}
          </div>
        </div> : null}
    </>;
}
function factIcon(label: string): BoxIcon {
  const key = label.toLowerCase();
  if (key.includes('start') || key.includes('date') || key.includes('intake')) return CalendarCheckIcon;
  if (key.includes('level')) return BarChart3Icon;
  if (key.includes('age')) return UsersIcon;
  if (key.includes('pack') || key.includes('plan')) return PackageIcon;
  if (key.includes('length') && !key.includes('lesson')) return CalendarRangeIcon;
  if (key.includes('lesson') || key.includes('hour') || key.includes('timetable')) return ClockIcon;
  return InfoIcon;
}
function FactList({
  facts
}: {
  facts: Fact[];
}) {
  return <dl className="grid grid-cols-2 gap-6 md:grid-cols-4" data-id="element-831" data-mp="35cn0akdivsg9">
      {facts.map(fact => {
      const Icon = factIcon(fact.label);
      return <div key={fact.label} className="flex gap-3" data-id="element-832" data-mp="3fbclon4mnv19">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[11px] bg-cream text-brand" aria-hidden="true" data-id="element-833" data-mp="3fcnl6ar1igmk">
              <Icon className="h-5 w-5" data-id="element-834" data-mp="2rjfcl3skbh3u" />
            </span>
            <div data-id="element-835" data-mp="2mo00ohe47va2">
              <dt className="type-small text-muted" data-id="element-836" data-mp="2mohznls5wnlo">{fact.label}</dt>
              <dd className="mt-0.5 type-h5 text-ink" data-id="element-837" data-mp="31cmkoweotb7m">{fact.value}</dd>
            </div>
          </div>;
    })}
    </dl>;
}